// FreeMirror — client-side face-swap try-on.
// Given a customer's photo + her face-mask circle + a pre-existing AI model
// photo (the "saree drape" that Gemini generated ONCE at catalog time), we
// composite her face pixels onto the model's face location. Zero AI calls at
// try-on time. Runs entirely in the browser in ~200-400 ms.

let _facePromise = null;  // memoized MediaPipe FaceDetector — one per page load
const _modelFaceCache = new Map(); // key = modelUrl, value = {x_pct, y_pct, r_pct} | null

async function _getFaceDetector() {
    if (_facePromise) return _facePromise;
    _facePromise = (async () => {
        try {
            const mod = await import(
                /* webpackIgnore: true */
                "https://cdn.jsdelivr.net/npm/@mediapipe/[email protected]/+esm"
            );
            const filesetResolver = await mod.FilesetResolver.forVisionTasks(
                "https://cdn.jsdelivr.net/npm/@mediapipe/[email protected]/wasm"
            );
            const detector = await mod.FaceDetector.createFromOptions(filesetResolver, {
                baseOptions: {
                    modelAssetPath: "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite",
                },
                runningMode: "IMAGE",
                minDetectionConfidence: 0.4,
            });
            return detector;
        } catch (e) {
            // Silent failure — caller falls back to the top-center heuristic.
            return null;
        }
    })();
    return _facePromise;
}

function _loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });
}

/** Detect the biggest face in an image; returns normalized {x_pct, y_pct, r_pct} or null. */
export async function detectFaceMask(imageUrl) {
    const detector = await _getFaceDetector();
    if (!detector) return null;
    let img;
    try { img = await _loadImage(imageUrl); }
    catch { return null; }
    try {
        const res = detector.detect(img);
        const dets = res?.detections || [];
        if (!dets.length) return null;
        // Pick the largest bounding box (nearest face).
        const box = dets
            .map((d) => d.boundingBox)
            .sort((a, b) => (b.width * b.height) - (a.width * a.height))[0];
        if (!box) return null;
        // MediaPipe returns pixel coords in the image space.
        const cx = box.originX + box.width / 2;
        const cy = box.originY + box.height / 2;
        // A slightly-expanded circle covers the whole face including chin + forehead.
        const r = Math.max(box.width, box.height) * 0.65;
        const W = img.naturalWidth || img.width;
        const H = img.naturalHeight || img.height;
        const S = Math.min(W, H);
        return {
            x_pct: Math.max(0, Math.min(1, cx / W)),
            y_pct: Math.max(0, Math.min(1, cy / H)),
            r_pct: Math.max(0.04, Math.min(0.5, r / S)),
        };
    } catch {
        return null;
    }
}

/** Cached wrapper — the model photo is the same for every customer, so we
    detect once and remember. */
export async function detectModelFaceCached(modelUrl) {
    if (_modelFaceCache.has(modelUrl)) return _modelFaceCache.get(modelUrl);
    const mask = await detectFaceMask(modelUrl);
    _modelFaceCache.set(modelUrl, mask);
    return mask;
}

// Fallback used when MediaPipe fails or the model face isn't detectable —
// most AI-generated portraits have the face at top-center of the frame.
const DEFAULT_MODEL_FACE = { x_pct: 0.5, y_pct: 0.22, r_pct: 0.13 };

/**
 * Composite the customer's face (defined by `customerMask`) onto the pre-made
 * model photo at the model's face location. Result approximates a try-on
 * without any AI call.
 *
 * @param {string} customerPhotoUrl  data URL of the customer's captured photo
 * @param {{x_pct,y_pct,r_pct}}     customerMask normalized circle around customer face
 * @param {string} modelPhotoUrl     data URL OR https URL of the pre-generated model photo
 * @returns {Promise<string>}        data URL of the composited image (JPEG 90%)
 */
export async function swapFaceOnModel(customerPhotoUrl, customerMask, modelPhotoUrl) {
    if (!customerPhotoUrl || !modelPhotoUrl) return modelPhotoUrl;
    const modelMask = (await detectModelFaceCached(modelPhotoUrl)) || DEFAULT_MODEL_FACE;

    const [customerImg, modelImg] = await Promise.all([
        _loadImage(customerPhotoUrl),
        _loadImage(modelPhotoUrl),
    ]);

    const W = modelImg.naturalWidth;
    const H = modelImg.naturalHeight;
    if (!W || !H) return modelPhotoUrl;

    // Base = the pre-generated model photo (already wearing the saree).
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(modelImg, 0, 0);

    // Target face box in model space.
    const S = Math.min(W, H);
    const tcx = modelMask.x_pct * W;
    const tcy = modelMask.y_pct * H;
    const tr = modelMask.r_pct * S;

    // Source face box in the customer's image — either from her drawn circle,
    // or auto-detected as a fallback.
    let cm = customerMask;
    if (!cm) cm = await detectFaceMask(customerPhotoUrl) || { x_pct: 0.5, y_pct: 0.3, r_pct: 0.15 };
    const cW = customerImg.naturalWidth;
    const cH = customerImg.naturalHeight;
    const cS = Math.min(cW, cH);
    const scx = cm.x_pct * cW;
    const scy = cm.y_pct * cH;
    const sr = cm.r_pct * cS;

    // Scale factor so the source face fills the target face circle.
    const scale = tr / sr;

    // Draw source face onto a temp canvas positioned so its center lands at
    // the target center, after scaling.
    const tmp = document.createElement("canvas");
    tmp.width = W;
    tmp.height = H;
    const tctx = tmp.getContext("2d");
    const dx = tcx - scx * scale;
    const dy = tcy - scy * scale;
    tctx.drawImage(customerImg, dx, dy, cW * scale, cH * scale);

    // Feathered circular alpha mask at the TARGET face location.
    tctx.globalCompositeOperation = "destination-in";
    const inner = tr * 0.72;
    const grad = tctx.createRadialGradient(tcx, tcy, inner, tcx, tcy, tr);
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(1, "rgba(255,255,255,0)");
    tctx.fillStyle = grad;
    tctx.fillRect(0, 0, W, H);

    // Blend patch over the model.
    ctx.drawImage(tmp, 0, 0);

    return canvas.toDataURL("image/jpeg", 0.9);
}
